import type { DatumAbstraction } from "building-types/datums/datumAbstractionTypes";
import type { HalfEdgeToFloorPath } from "building-types/floors/floorSystemTypes";
import type { SheathingComponentEntity } from "building-types/floors/sheathingComponentTypes";
import type { SlabComponentEntity } from "building-types/floors/slabComponentTypes";
import { isSolidFoundationAssembly } from "building-types/foundations/foundationAssemblyEntityTypes";
import { FoundationAssemblyType } from "building-types/foundations/foundationAssemblyTypesEnum";
import type { FoundationComponentEntity } from "building-types/foundations/foundationComponentEntityTypes";
import type {
  CurbEntity,
  FootingEntity,
  StemWallEntity,
} from "building-types/foundations/foundationComponentEntityTypes";
import {
  isRectangularIsolatedFooting,
  isCircularIsolatedFooting,
} from "building-types/foundations/foundationComponents";
import type { FoundationSubSystemType } from "building-types/foundations/foundationEnums";
import { FootingType, PierType, StemWallType } from "building-types/foundations/foundationEnums";
import { FoundationSectionSizeType } from "building-types/foundations/foundationEnums";
import type {
  FoundationPath,
  FoundationSectionSize,
  FoundationSubSystem,
  IntermediateExtrusionFrameInfo,
  IntermediateFoundationSection,
  IntermediateFoundationStrip,
  IntermediateFoundationSubSystem,
  HalfEdgeToIntermediateFoundationStrip,
  HalfEdgeToFoundationPath,
  HalfEdgeToFoundationStrip,
  LinearFoundationSectionSize,
} from "building-types/foundations/foundationTypes";
import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { DoorSettings } from "building-types/settings/doorSettings";
import type { SlabSettings } from "building-types/settings/foundationSettingsTypes";
import type { HalfEdgeOffsets, WallEndButtJoin, WallEndMiterJoin } from "building-types/walls/wallEndsTypes";
import { ButtCapMode, WallEndJoinType } from "building-types/walls/wallEndsTypes";
import { WallType } from "building-types/walls/wallEnums";
import type { WallPathBase } from "building-types/walls/wallTypes";
import type { Table } from "common/data/table";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { assertUnreachable } from "common/utils/assertUnreachable";
import uid from "common/utils/uid";
import { DIST_EPSILON_SQUARED } from "geom/core";
import {
  planarGraphAdjacentHalfEdge as ahe,
  planarGraphHalfEdgeEndVertex,
  planarGraphHalfEdgeStartVertex,
} from "geom/planarGraph";
import { planeNewXY, planeReProjectPolyLine2 } from "geom/plane";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import { polyLine2sAreCoincident } from "geom/polyLine2";
import { vec2DistSquared } from "geom/vec2";
import { halfEdgeOffsetsNew, makeWallEndCaps, makeWallEndCapSources } from "../walls/wallEnds";
import { makeCurbSectionSizes } from "./curb";
import { makeIsolatedFootingSize, makeStripFootingSectionSizes, makeTurndownFootingSectionSizes } from "./footing";
import { buildFoundationAssemblyComponents, isVoidFoundationAssembly } from "./foundationAssemblyTools";
import type { FoundationPathEnds } from "./foundationStrip";
import {
  buildFoundationSectionExtrusions,
  foundationSectionSizeOpposite,
  foundationSectionSizeToWallButtCapExtension,
  foundationSectionSizeToWallOffsetInfo,
  foundationSectionSizeToWallPathBase,
  isIsolatedFoundationSectionSize,
  makeIsolatedFoundationSection,
  makeLinearFoundationSection,
} from "./foundationStrip";
import { foundationPathCoordinates } from "./pathCoordinates";
import { makeIsolatedPierSize } from "./pier";
import { makeStemWallSectionSizes } from "./stemWall";

export interface MakeFoundationSystemOptions {
  readonly level: Level;
  readonly planarGraph: PolygonPlanarGraph;
  readonly planarGraphs: Table<PolygonPlanarGraph>;
  readonly doorSettings: DoorSettings;
  readonly datumAbstractions: Table<DatumAbstraction>;
  readonly slabSettings?: SlabSettings;
  readonly foundationComponents: Table<FoundationComponentEntity>;
  readonly framingComponents: Table<FramingComponentEntity>;
  readonly slabComponents: Table<SlabComponentEntity>;
  readonly sheathingComponents: Table<SheathingComponentEntity>;
  readonly issues: BuildingGenerationIssue[];
  readonly floorPaths?: HalfEdgeToFloorPath;
  readonly foundationSubSystemType: FoundationSubSystemType;
  readonly entityHandleForSelectingReportingUnit?: DocEntityHandle;
}

export function makeFoundationSubSystem(
  paths: HalfEdgeToFoundationPath,
  opts: MakeFoundationSystemOptions,
): FoundationSubSystem {
  const intermediate = makeIntermediateFoundationSubSystem(paths, opts);
  return buildFoundationSubSystemExtrusions(intermediate);
}

export function makeIntermediateFoundationSubSystem(
  paths: HalfEdgeToFoundationPath,
  opts: MakeFoundationSystemOptions,
): IntermediateFoundationSubSystem {
  const strips = makeIntermediateFoundationStrips(paths, opts);
  return {
    type: opts.foundationSubSystemType,
    strips,
    entityHandleForSelectingReportingUnit: opts.entityHandleForSelectingReportingUnit,
  };
}

export function buildFoundationSubSystemExtrusions(
  intermediateSubSystem: IntermediateFoundationSubSystem,
): FoundationSubSystem {
  const strips: HalfEdgeToFoundationStrip = {};
  for (const [halfEdgeIndex, strip] of Object.entries(intermediateSubSystem.strips)) {
    strips[Number(halfEdgeIndex)] = {
      ...strip,
      sections: strip.sections.map((section) => buildFoundationSectionExtrusions(section)),
    };
  }
  return {
    type: intermediateSubSystem.type,
    strips,
    entityHandleForSelectingReportingUnit: intermediateSubSystem.entityHandleForSelectingReportingUnit,
  };
}

export function convertFoundationSubSystemToIntermediate(
  foundationSubSystem: FoundationSubSystem,
): IntermediateFoundationSubSystem {
  const worldXYPlane = planeNewXY();
  const strips: HalfEdgeToIntermediateFoundationStrip = {};
  for (const [halfEdgeIndex, strip] of Object.entries(foundationSubSystem.strips)) {
    const halfEdgeIndexNumber = Number(halfEdgeIndex);
    const sections: IntermediateFoundationSection[] = strip.sections.map((section, sectionIndex) => {
      const extrusionDescriptors = section.extrusions.map((extrusion, index) => {
        const sourcePlane = extrusion.profile.plane;
        const top = extrusion.profile.plane.originZ;
        const depth = Math.abs(extrusion.dirZ);
        const worldProfileLoops = extrusion.profile.loops.map((loop) =>
          planeReProjectPolyLine2(loop, sourcePlane, worldXYPlane),
        );
        const outerLoopInWorld = worldProfileLoops[0];
        const includeInPlanProfiles = outerLoopInWorld
          ? section.planProfiles.some((planProfile) => polyLine2sAreCoincident(planProfile, outerLoopInWorld))
          : false;
        const frameInfo: IntermediateExtrusionFrameInfo = {
          sourceDescriptorId: `${strip.id}:${section.id}:${index}`,
          halfEdgeIndex: halfEdgeIndexNumber,
          sectionId: section.id,
          sectionIndex,
          extrusionIndex: index,
          sourceDirX: extrusion.dirX,
          sourceDirY: extrusion.dirY,
          sourceDirZ: extrusion.dirZ,
          sourcePlane: {
            originX: worldXYPlane.originX,
            originY: worldXYPlane.originY,
            originZ: worldXYPlane.originZ,
            uX: worldXYPlane.uX,
            uY: worldXYPlane.uY,
            uZ: worldXYPlane.uZ,
            vX: worldXYPlane.vX,
            vY: worldXYPlane.vY,
            vZ: worldXYPlane.vZ,
          },
        };
        return {
          profile: worldProfileLoops,
          top,
          depth,
          includeInPlanProfiles,
          material: section.material,
          frameInfo,
        };
      });
      return {
        ...section,
        extrusionDescriptors,
      };
    });
    strips[Number(halfEdgeIndex)] = {
      ...strip,
      sections,
    };
  }

  return {
    type: foundationSubSystem.type,
    strips,
    entityHandleForSelectingReportingUnit: foundationSubSystem.entityHandleForSelectingReportingUnit,
  };
}

function makeIntermediateFoundationStrips(
  paths: HalfEdgeToFoundationPath,
  opts: MakeFoundationSystemOptions,
): HalfEdgeToIntermediateFoundationStrip {
  const { planarGraph } = opts;
  const { halfEdges } = planarGraph.planarGraph;
  const halfEdgeToFoundationStrip: HalfEdgeToIntermediateFoundationStrip = {};

  const wallPaths: { [key: string]: WallPathBase[] } = {};
  const wallSectionSizes: { [key: string]: FoundationSectionSize[] } = {};
  const wallPathFill = (wallPaths: WallPathBase[]) => {
    for (let i = 0; i < wallPaths.length; i++) {
      wallPaths[i] = wallPaths[i] ?? { halfEdgeIndex: i, wallType: WallType.VOID };
    }
    return wallPaths;
  };

  // utility methods

  const addFoundationSection = (section: IntermediateFoundationSection) => {
    const { path } = section;
    const [startX, startY, endX, endY] = foundationPathCoordinates(path, opts.planarGraphs);

    if (vec2DistSquared(startX, startY, endX, endY) < DIST_EPSILON_SQUARED) {
      return;
    }
    const halfEdgeIndex = path.halfEdgeIndex;

    if (!halfEdgeToFoundationStrip[halfEdgeIndex]) {
      halfEdgeToFoundationStrip[halfEdgeIndex] = {
        id: uid(),
        path,
        sections: [],
      } as IntermediateFoundationStrip;
    }

    halfEdgeToFoundationStrip[halfEdgeIndex].sections.push(section);
  };

  const addFoundationSectionSize = (halfEdgeIndex: number, sectionSize: FoundationSectionSize) => {
    const { sectionId } = sectionSize;
    const wallPath = foundationSectionSizeToWallPathBase(halfEdgeIndex, sectionSize);

    assertIsNotUndefined(sectionId);

    if (!wallSectionSizes[sectionId]) {
      wallSectionSizes[sectionId] = [];
    }

    if (!wallPaths[sectionId]) {
      wallPaths[sectionId] = new Array(halfEdges.length);
    }

    if (!wallPaths[sectionId][halfEdgeIndex]) {
      wallPaths[sectionId][halfEdgeIndex] = wallPath;
    }

    if (!wallSectionSizes[sectionId][halfEdgeIndex]) {
      wallSectionSizes[sectionId][halfEdgeIndex] = sectionSize;
    }
  };

  // strip creation

  // iterate through all paths and create all section sizes per path on their own layer
  for (let i = 0, l = halfEdges.length; i < l; i += 2) {
    const halfEdgeIndex = i / 2;
    const oppHalfEdgeIndex = ahe(halfEdgeIndex);

    const path = paths[halfEdgeIndex];
    if (!path) {
      continue;
    }

    const sectionSizes = makeFoundationPathSubSystem(path, paths, opts);
    for (const sectionSize of sectionSizes) {
      addFoundationSectionSize(halfEdgeIndex, sectionSize);
      addFoundationSectionSize(oppHalfEdgeIndex, foundationSectionSizeOpposite(sectionSize));
    }
  }

  const foundationCornerJoin: WallEndMiterJoin = {
    joinType: WallEndJoinType.MITER,
    shiftTJunction: true,
  };

  const voidSizeOffsets: HalfEdgeOffsets = halfEdgeOffsetsNew(0, 0);

  const halfEdgeToWallOffsetInfo = (
    halfEdge: number,
    sectionSizes: FoundationSectionSize[],
    sectionPaths: WallPathBase[],
  ): HalfEdgeOffsets => {
    const sectionPath = sectionPaths[halfEdge];
    if (sectionPath.wallType === WallType.VOID) {
      return voidSizeOffsets;
    }
    const sectionSize = sectionSizes[halfEdge] as LinearFoundationSectionSize;
    return foundationSectionSizeToWallOffsetInfo(sectionSize);
  };

  const halfEdgeToWallFreeEndJoin = (
    halfEdge: number,
    sectionSizes: FoundationSectionSize[],
    sectionPaths: WallPathBase[],
  ): WallEndButtJoin => {
    let buttCapExtension = 0;
    const sectionPath = sectionPaths[halfEdge];
    if (sectionPath.wallType !== WallType.VOID) {
      const sectionSize = sectionSizes[halfEdge] as LinearFoundationSectionSize;
      buttCapExtension = foundationSectionSizeToWallButtCapExtension(sectionSize);
    }
    return {
      joinType: WallEndJoinType.BUTT,
      buttCapMode: ButtCapMode.END_ALIGNED,
      buttCapWrap: 0,
      buttCapExtension,
    };
  };

  // iterate through all the section types that have been created and generate the appropriate walls for them,
  // where the wall offsets/ends are dictated by the section sizes
  for (const sectionId in wallPaths) {
    const sectionPaths = wallPathFill(wallPaths[sectionId]); // fill missing paths with VOID
    const sectionSizes = wallSectionSizes[sectionId];
    const sectionSources = makeWallEndCapSources(
      {
        getWallPath: (halfEdge) => {
          return sectionPaths[halfEdge];
        },
        getWallEndCapOffsets: (halfEdge) => {
          return halfEdgeToWallOffsetInfo(halfEdge, sectionSizes, sectionPaths);
        },
        getWallTJunctionOffsets: (halfEdge) => {
          return halfEdgeToWallOffsetInfo(halfEdge, sectionSizes, sectionPaths);
        },
        getWallMiterJoin: (_halfEdge) => {
          return foundationCornerJoin;
        },
        getWallFreeEndJoin: (halfEdge) => {
          return halfEdgeToWallFreeEndJoin(halfEdge, sectionSizes, sectionPaths);
        },
      },
      planarGraph.planarGraph,
    );
    const sectionEnds = makeWallEndCaps({
      wallEndCapSources: sectionSources,
      planarGraph: planarGraph.planarGraph,
    });

    const halfEdgeVisited = new Array<boolean>(halfEdges.length);
    for (let i = 0, l = halfEdges.length; i < l; i += 2) {
      const halfEdgeIndex = i / 2;

      if (halfEdgeVisited[halfEdgeIndex]) {
        continue;
      }

      const oppHalfEdgeIndex = ahe(halfEdgeIndex);

      const size = sectionSizes[halfEdgeIndex];
      const path = paths[halfEdgeIndex];
      const startCap = sectionEnds[halfEdgeIndex];
      const endCap = sectionEnds[oppHalfEdgeIndex];

      if (!size || !path || !startCap || !endCap) {
        continue;
      }

      switch (size.sizeType) {
        case FoundationSectionSizeType.LINEAR:
          addFoundationSection(
            makeLinearFoundationSection({
              foundationPath: path,
              startCap,
              endCap,
              size,
            }),
          );
          break;
        case FoundationSectionSizeType.ISOLATED:
          const isolatedFoundationSection = makeIsolatedFoundationSection({
            foundationPath: path,
            size,
            planarGraphs: opts.planarGraphs,
          });
          if (isolatedFoundationSection) {
            addFoundationSection(isolatedFoundationSection);
          }
          break;
        case FoundationSectionSizeType.VOID:
          continue;
        default:
          assertUnreachable(size);
      }

      halfEdgeVisited[halfEdgeIndex] = true;
      halfEdgeVisited[oppHalfEdgeIndex] = true;
    }
  }

  return halfEdgeToFoundationStrip;
}

function makeFoundationPathSubSystem(
  path: FoundationPath,
  paths: HalfEdgeToFoundationPath,
  opts: MakeFoundationSystemOptions,
) {
  const {
    level,
    doorSettings,
    slabSettings,
    foundationComponents,
    floorPaths,
    framingComponents,
    slabComponents,
    sheathingComponents,
  } = opts;

  const { halfEdgeIndex, assembly, isolatedAssemblies, planarGraphId } = path;
  const pathPlanarGraph = opts.planarGraphs[planarGraphId];
  if (!pathPlanarGraph) {
    throw new Error(
      `Foundation path planar graph not found for path halfEdgeIndex: ${halfEdgeIndex} (planarGraphId: ${planarGraphId})`,
    );
  }

  //we have now separated the floor paths from the foundation paths,
  //so we need to find the floor path that corresponds to the foundation path
  //they both should come from the same planar graph in this implementation so far and we check
  //to make sure they have the same planar gaph id. If not then we don't have a floor path for this
  //foundation path. The foundation subSystem code is updated to handle this now.
  //Note - The derivation of the floor path for a give foundation path does not necessarily need to be done this
  //way and can be an abstraction too i.e we pass in halfEdgeIndex and planarGraphId from the foundation path and
  //get the floor paths or anything else we need back.
  const floorPath =
    floorPaths?.[halfEdgeIndex]?.planarGraphId === planarGraphId ? floorPaths?.[halfEdgeIndex] : undefined;

  const halfEdgeSectionSizes: FoundationSectionSize[] = [];
  const { planarGraph } = pathPlanarGraph;

  const halfEdgeStartVertex = planarGraphHalfEdgeStartVertex(planarGraph, halfEdgeIndex);
  const halfEdgeStartPaths = planarGraph.vertexHalfEdges[halfEdgeStartVertex].map((ohe) => paths[ohe]);

  //we need to find the startfloor path that corresponds to the foundation path
  const halfEdgeStartFloorPaths = floorPaths
    ? planarGraph.vertexHalfEdges[halfEdgeStartVertex]
        .map((ohe) => (floorPaths?.[ohe]?.planarGraphId === planarGraphId ? floorPaths?.[ohe] : undefined))
        .filter((x) => x !== undefined)
    : [];

  const halfEdgeEndVertex = planarGraphHalfEdgeEndVertex(planarGraph, halfEdgeIndex);
  const halfEdgeEndPaths = planarGraph.vertexHalfEdges[halfEdgeEndVertex].map((ohe) => paths[ohe]);

  //we need to find the end floor path that corresponds to the foundation path
  const halfEdgeEndFloorPaths = floorPaths
    ? planarGraph.vertexHalfEdges[halfEdgeEndVertex]
        .map((ohe) => (floorPaths?.[ohe]?.planarGraphId === planarGraphId ? floorPaths?.[ohe] : undefined))
        .filter((x) => x !== undefined)
    : [];

  // Group foundation and floor paths together for cleaner function signatures
  const pathEnds: FoundationPathEnds = {
    fromStart: {
      foundationPaths: halfEdgeStartPaths,
      floorPaths: halfEdgeStartFloorPaths,
    },
    fromEnd: {
      foundationPaths: halfEdgeEndPaths,
      floorPaths: halfEdgeEndFloorPaths,
    },
  };

  let stemWall: StemWallEntity | undefined;
  let footing: FootingEntity | undefined;
  let curb: CurbEntity | undefined;

  if (isSolidFoundationAssembly(assembly)) {
    const foundationAssemblyComponents = buildFoundationAssemblyComponents(foundationComponents, assembly);
    stemWall = foundationAssemblyComponents.stemWall;
    footing = foundationAssemblyComponents.footing;
    curb = foundationAssemblyComponents.curb;
  }

  for (const isolatedAssembly of isolatedAssemblies) {
    if (isVoidFoundationAssembly(isolatedAssembly)) {
      continue;
    }

    const { pier, footing } = buildFoundationAssemblyComponents(foundationComponents, isolatedAssembly);

    if (footing) {
      switch (footing.footingType) {
        case FootingType.RECTANGULAR_ISOLATED:
        case FootingType.CIRCULAR_ISOLATED: {
          halfEdgeSectionSizes.push(
            ...[
              makeIsolatedFootingSize({
                level,
                foundationPath: path,
                floorPath,
                footing,
                pier,
                isolatedAssembly,
                planarGraphs: opts.planarGraphs,
              }),
            ],
          );
          break;
        }
        case FootingType.TURNDOWN:
        case FootingType.STRIP:
          break;
        default: {
          assertUnreachable(footing);
        }
      }
    }

    if (pier) {
      switch (pier.pierType) {
        case PierType.CIRCULAR_CIP:
        case PierType.RECTANGULAR_CIP:
        case PierType.CIRCULAR_CMU:
        case PierType.RECTANGULAR_CMU:
        case PierType.CUSTOM: {
          if (footing && (isRectangularIsolatedFooting(footing) || isCircularIsolatedFooting(footing))) {
            halfEdgeSectionSizes.push(
              ...[
                makeIsolatedPierSize({
                  level,
                  foundationPath: path,
                  floorPath,
                  footing,
                  pier,
                  slabSettings,
                  isolatedAssembly,
                  stemWall,
                  planarGraphs: opts.planarGraphs,
                }),
              ],
            );
          }
          break;
        }
        default: {
          assertUnreachable(pier);
        }
      }
    }
  }

  if (assembly.assemblyType === FoundationAssemblyType.VOID) {
    return halfEdgeSectionSizes;
  }

  if (curb) {
    halfEdgeSectionSizes.push(
      ...makeCurbSectionSizes({
        level,
        foundationPath: path,
        floorPath,
        pathEnds,
        curb,
        doorSettings,
        planarGraphs: opts.planarGraphs,
      }),
    );
  }

  if (footing) {
    switch (footing.footingType) {
      case FootingType.TURNDOWN: {
        halfEdgeSectionSizes.push(
          ...makeTurndownFootingSectionSizes({
            level,
            foundationPath: path,
            floorPath,
            pathEnds,
            footing,
            slabSettings,
            doorSettings,
            isolatedFoundationSectionSizes: halfEdgeSectionSizes.filter(isIsolatedFoundationSectionSize),
            planarGraphs: opts.planarGraphs,
          }),
        );
        break;
      }
      case FootingType.STRIP: {
        if (!stemWall) {
          break;
        }
        halfEdgeSectionSizes.push(
          ...makeStripFootingSectionSizes({
            level,
            foundationPath: path,
            floorPath,
            pathEnds,
            footing,
            stemWall,
            isolatedFoundationSectionSizes: halfEdgeSectionSizes.filter(isIsolatedFoundationSectionSize),
            planarGraphs: opts.planarGraphs,
          }),
        );
        break;
      }
      //these currently only are defined for pier assemblies
      case FootingType.RECTANGULAR_ISOLATED:
      case FootingType.CIRCULAR_ISOLATED: {
        break;
      }
      default: {
        assertUnreachable(footing);
        break;
      }
    }
  }

  if (stemWall) {
    switch (stemWall.stemWallType) {
      case StemWallType.CIP:
      case StemWallType.CMU: {
        if (!footing || footing.footingType !== FootingType.STRIP) {
          break;
        }
        halfEdgeSectionSizes.push(
          ...makeStemWallSectionSizes({
            level,
            foundationPath: path,
            floorPath,
            pathEnds,
            stemWall,
            footing,
            slabSettings,
            doorSettings,
            framingComponents,
            slabComponents,
            sheathingComponents,
            isolatedFoundationSectionSizes: halfEdgeSectionSizes.filter(isIsolatedFoundationSectionSize),
            planarGraphs: opts.planarGraphs,
          }),
        );
        break;
      }
      default: {
        assertUnreachable(stemWall);
      }
    }
  }

  return halfEdgeSectionSizes;
}
